import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ProductCard, type Product } from '@/components/product/ProductCard';
import { useFavourites, type FavItem } from '@/store/favourites';
import { useAuthStore } from '@/store/auth';
import { api } from '@/lib/api';

export default function Saved() {
  const items = useFavourites((s) => s.items);
  const remove = useFavourites((s) => s.remove);
  const clear = useFavourites((s) => s.clear);
  const hydrate = useFavourites((s) => s.hydrate);
  const user = useAuthStore((s) => s.user);

  // Local storage only survives on this device - pull the real saved list from
  // the server (kept in sync by the heart button) so it's the same on any device.
  const { data: serverSaved } = useQuery<FavItem[]>({
    queryKey: ['watches'],
    queryFn: async () => (await api.get('/me/watches')).data,
    enabled: !!user,
    retry: false,
  });
  useEffect(() => { if (serverSaved) hydrate(serverSaved); }, [serverSaved, hydrate]);

  function removeItem(id: string) {
    remove(id);
    if (user) api.delete(`/me/watch/${id}`).catch(() => undefined);
  }
  function clearAll() {
    if (user) items.forEach((it) => api.delete(`/me/watch/${it.id}`).catch(() => undefined));
    clear();
  }

  if (!items.length) {
    return (
      <div className="bg-cream min-h-[70vh]">
        <div className="max-w-[680px] mx-auto px-6 py-20 text-center">
          <div className="text-5xl mb-4">🤍</div>
          <h1 className="display text-4xl">Nothing saved yet</h1>
          <p className="text-mute text-sm mt-2">Tap the heart on any product to save it here for later.</p>
          <Link to="/c/all" className="btn btn-primary mt-6">Browse the marketplace</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-[70vh]">
      <div className="max-w-[1800px] mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="stamp text-gold-dark text-[9px]">SAVED</div>
            <h1 className="display text-4xl mt-1">{items.length} saved item{items.length > 1 ? 's' : ''}</h1>
          </div>
          <button
            onClick={() => { clearAll(); toast.success('Cleared all saved items.'); }}
            className="text-sm text-mute hover:text-bad underline underline-offset-2"
          >
            Clear all
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-3 gap-y-8 mt-8">
          {items.map((it) => (
            <div key={it.id} className="relative group">
              <ProductCard product={it as unknown as Product} />
              <button
                onClick={() => { removeItem(it.id); toast('Removed from saved.', { icon: '🗑️' }); }}
                aria-label={`Remove ${it.title} from saved`}
                title="Remove from saved"
                className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-ink/80 text-cream grid place-items-center text-sm shadow-luxe hover:bg-bad transition opacity-0 group-hover:opacity-100 focus:opacity-100"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-mute mt-6">Hover a card and tap ✕ to remove it, or tap the heart on the item.</p>
      </div>
    </div>
  );
}
